import { DateTime } from 'luxon';

/**
 * A company's QuickBooks date and currency formats, as the API saves them
 * (see the API's services/company-format.ts). Missing values fall back to US.
 *   date:     a Luxon pattern, e.g. "MM/dd/yyyy"
 *   number:   a sample showing the separators, e.g. "1,234.56" or "1.234,56"
 *   currency: the home currency code, e.g. "USD"
 */
export type CompanyFormat = { date?: string | null; number?: string | null; currency?: string | null };

const NUMBER_SAMPLES = ['1,234.56', '1.234,56', '1 234,56', "1'234.56", '1 234.56'];

const withDefaults = (format?: CompanyFormat) => ({
  date: format?.date || 'MM/dd/yyyy',
  number: NUMBER_SAMPLES.includes(format?.number || '') ? (format?.number as string) : '1,234.56',
  currency: format?.currency || 'USD'
});

export const datePattern = (format?: CompanyFormat) => withDefaults(format).date;

/** "2026-11-02" in the company's date format. Other text is returned unchanged. */
export function formatDate(isoDate: string, format?: CompanyFormat) {
  const date = DateTime.fromISO(isoDate || '', { setZone: true });
  return date.isValid ? date.toFormat(datePattern(format)) : isoDate;
}

function currencySymbol(currency: string) {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol'
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value || currency;
  } catch {
    return currency;
  }
}

/** 1201 as "$1,201.00" in the company's currency format. */
export function formatMoney(amount: number, format?: CompanyFormat) {
  const { number, currency } = withDefaults(format);
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return String(amount);
  const [group, decimal] = [number[1], number[5]];
  const [whole, cents] = Math.abs(amount).toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  const negative = amount < 0 && Number(Math.abs(amount).toFixed(2)) !== 0;
  return `${negative ? '-' : ''}${currencySymbol(currency)}${grouped}${decimal}${cents}`;
}
